import { AlertTriangle, Check, Copy, Info, Loader2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

const tones = {
  neutral: 'bg-surface-hover text-ink-secondary',
  success: 'bg-[color:var(--feedback-success-bg)] text-success',
  warning: 'bg-[color:var(--feedback-warning-bg)] text-warning',
  danger: 'bg-[color:var(--feedback-error-bg)] text-danger',
  info: 'bg-[color:var(--feedback-info-bg)] text-info',
  brand: 'bg-brand-soft text-brand',
} as const
export type Tone = keyof typeof tones

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>{children}</span>
}

export function Notice({ tone = 'info', children, title }: { tone?: 'info' | 'warning' | 'danger' | 'success'; children: ReactNode; title?: ReactNode }) {
  const Icon = tone === 'info' ? Info : tone === 'success' ? Check : AlertTriangle
  return (
    <div className={`flex gap-2.5 rounded-xl border border-line px-3.5 py-3 text-[12.5px] ${tones[tone]}`}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="min-w-0 text-ink-secondary">
        {title && <div className="mb-0.5 font-semibold text-ink">{title}</div>}
        {children}
      </div>
    </div>
  )
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 p-6 text-[13px] text-ink-muted">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label ?? 'Loading…'}
    </div>
  )
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      {icon && <div className="mb-3 text-ink-disabled">{icon}</div>}
      <div className="text-[14px] font-semibold text-ink">{title}</div>
      {children && <div className="mt-1 max-w-sm text-[12.5px] text-ink-muted">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function CopyButton({ value, label = 'Copy', className = '' }: { value: string; label?: string; className?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      className={`btn-ghost !h-7 !px-2 !text-[11.5px] ${className}`}
      onClick={async () => {
        await navigator.clipboard.writeText(value)
        setDone(true)
        setTimeout(() => setDone(false), 1500)
      }}
    >
      {done ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      {done ? 'Copied' : label}
    </button>
  )
}

export function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${checked ? 'bg-brand' : 'bg-[color:var(--border-strong)]'}`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
    </button>
  )
}

export function Field({ label, hint, children, error }: { label: ReactNode; hint?: ReactNode; children: ReactNode; error?: string | null }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-[11.5px] text-ink-muted">{hint}</span>}
      {error && <span className="mt-1 block text-[11.5px] text-danger">{error}</span>}
    </label>
  )
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-5 md:px-8">
      <div className="min-w-0">
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  )
}

/** A figure with its label; no box. Put several in a StatRow. */
export function Stat({ label, value, hint }: { label: ReactNode; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="min-w-0 py-1">
      <div className="eyebrow !text-[10.5px]">{label}</div>
      <div className="mt-1.5 text-[24px] font-medium tabular-nums tracking-[-0.02em] text-ink">{value}</div>
      {hint && <div className="mt-0.5 text-[11.5px] text-ink-muted">{hint}</div>}
    </div>
  )
}

/** Figures side by side between two hairlines, divided by hairlines. */
export function StatRow({ children, flush }: { children: ReactNode; /** no top line (right under a page header) */ flush?: boolean }) {
  return (
    <div className={`${flush ? 'border-b pb-4' : 'border-y py-4'} grid grid-cols-2 gap-y-3 border-line-subtle sm:auto-cols-fr sm:grid-flow-col sm:grid-cols-none sm:divide-x sm:divide-[color:var(--border-subtle)] [&>*]:sm:px-5 [&>*:first-child]:sm:pl-0`}>
      {children}
    </div>
  )
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null
  const e = error as { message?: string; details?: string[] }
  return (
    <div className="rounded-md bg-[color:var(--feedback-error-bg)] px-3 py-2 text-[12px] text-danger">
      {e.message ?? String(error)}
      {e.details?.length ? (
        <ul className="mt-1 list-disc pl-4">
          {e.details.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
